import { observer } from "mobx-react-lite";
import { Fragment, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import FontBody2 from "../../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR } from "../../../../components/fonts/font-props";
import IconConfig from "../../../../components/icons/icon-config/icon-config";
import IconTranslationIndicator
	from "../../../../components/icons/icon-translation-indicator/icon-translation-indicator";
import Icon from "../../../../components/icons/icon/icon";
import { ButtonIcon } from "../../../../components/inputs/button-icon/button-icon";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import { Tooltip, TOOLTIP_VARIANTS } from "../../../../components/tooltip/tooltip";
import { ExpandCallapseStore } from "../../../../hooks/expand-collapse-hook";
import { FEATURES } from "../../../../models/Plan";
import { RESOLUTION_CATEGORY } from "../../../../models/ResolutionTypes";
import currentPortalStore from "../../../../stores/current-portal-store";
import useTranslation from "../../../../translation/hooks/translation-hook";
import resolutionListStore, { ResolutionsCategoryStore } from "../../stores/resolution-list-store";
import { ConfigurationModal } from "../configuration-modal/configuration-modal";
import ResolutionsCard from "./resolutions-card/resolution-card";
import './resolutions-list.scss';
import dashboardSettingsStore from "../../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../../models/ProfileCapabilities";


const ResolutionsList: React.FC = observer(() => {
    const navigate = useNavigate();
    const { setSubTitles, setInstruction } = usePage();
    const [showConfigurationModal, setShowConfigurationModal] = useState(false);

    const setting = currentPortalStore.setting;
    const expandCollapseStore = useOutletContext<ExpandCallapseStore>();
    const { setTranslatableKeys, hasMissingTranslations } = useTranslation();
    const resolutionCategories = resolutionListStore.resolutions;

    useEffect(() => {
        setSubTitles(['Resolutions']);
    }, [setSubTitles]);

    useEffect(() => {
        const keys: string[] = [];
        resolutionCategories.forEach(resolutionsCategory => {
            resolutionsCategory.filteredList.forEach(resolution => {
                keys.push(...resolution.getTranslationKeys())
            });            
        });
        setTranslatableKeys(keys);

        return () => setTranslatableKeys([]);
    }, [resolutionCategories, setTranslatableKeys]);

    useEffect(() => {
        setInstruction('Define the types of returns you want to offer to customers.');
    });

    const textByCategoryMap = {
        [RESOLUTION_CATEGORY.exchange_resolutions]: {
            title: 'Exchange Resolutions',
            description: 'Define how customers are able to exchange purchased items',
        },
        [RESOLUTION_CATEGORY.standard_resolutions]: {
            title: 'Return Resolutions',
            description: 'Define how your customers get refunded for returned items.',
        },
        [RESOLUTION_CATEGORY.returngo_resolutions]: {
            title: 'ReturnScore Instant Credit Resolutions',
            description: 'ReturnScore is an AI-based refund offering system. The AI will offer customers Store Credit refunds based on the customer’s history and your configurations. Customers who will choose these resolutions will be immediately issued the offered refund, which will improve customer satisfaction, retention, and average order value',
        },
        [RESOLUTION_CATEGORY.cancel_order_resolutions]: {
            title: 'Order Modification Resolutions',
            description: 'Explain your order canceling policy to customers.',
        },
        [RESOLUTION_CATEGORY.other_resolutions]: { /** this resolution category is not fetched for resolutions editor screen - not configurable from UI */
            title: 'No Offers',
            description: '----',
        },
    }

    const isCardSupported = (resolutionCategory: ResolutionsCategoryStore): boolean => {
      const instantCreditsResolutionCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.INSTANT_CREDIT_RESOLUTION);

      if (resolutionCategory.category === RESOLUTION_CATEGORY.returngo_resolutions) {
        return (instantCreditsResolutionCapability.isSupported && instantCreditsResolutionCapability.isFeatureEnabled) 
             || (!!currentPortalStore.platform?.isFeatureSupported(FEATURES.RETURN_SCORE_RESOLUTION));
      }

      if (resolutionCategory.category === RESOLUTION_CATEGORY.cancel_order_resolutions) {
        const cancelOrderCapability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.CANCEL_ORDER);
        return cancelOrderCapability.isSupported;
      }

      return true;
    }

    const getToolbarContent = (resolutionCategory: ResolutionsCategoryStore) => {            
        const categoryTranslationKeys: string[] = [];
        resolutionCategory.filteredList.forEach(resolution => {
            categoryTranslationKeys.push(...resolution.getTranslationKeys())
        });   

        return (
            <div className="card-toolbar-buttons">
                {hasMissingTranslations(undefined, categoryTranslationKeys) &&
                    <Tooltip
                        content={
                            <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                There are untranslated items. Please edit <br />
                                the resolution for more information.
                            </FontBody2>
                        }
                        variant={TOOLTIP_VARIANTS.ERROR}
                    >
                        <Icon Component={IconTranslationIndicator} />
                    </Tooltip>
                }
                {(resolutionCategory.category === RESOLUTION_CATEGORY.returngo_resolutions) &&                    
                    <ButtonIcon Component={IconConfig} onClick={() => setShowConfigurationModal(true)} />                    
                }
            </div>
        )
    }

    const onAddResolutionClicked = (resolutionCategory: RESOLUTION_CATEGORY) => {
        const resolutionId = resolutionListStore.addResolution(resolutionCategory);

        if (!resolutionId) {
            /* this should never happen */
            throw new Error('onAddResolutionClicked was not able to generate new reason');
        }

        navigate({
            pathname: resolutionId
        });
    }

    return <>
        <EditorPageLayout
            toolbar={{
                hasBack: false,
                expandAllButton: {
                    expanded: expandCollapseStore.isAllExpanded(),
                    onClick: expandCollapseStore.toggleAllExpanded,
                },
                isLoading: resolutionListStore.isLoading || currentPortalStore.isLoading
            }}
        >
            <div className="resolutions-list">
                {Object.values(resolutionListStore.resolutions).map(resolutionCategory =>
                    <Fragment key={resolutionCategory.category}>
                        {isCardSupported(resolutionCategory) &&
                            <ResolutionsCard
                                title={textByCategoryMap[resolutionCategory.category].title}
                                description={textByCategoryMap[resolutionCategory.category].description}
                                toolbarContent={getToolbarContent(resolutionCategory)}
                                isExpanded={expandCollapseStore.getIsExpanded(resolutionCategory.category)}
                                onExpandClick={(v: boolean) => expandCollapseStore.setExpanded(resolutionCategory.category, v)}
                                resolutionCategory={resolutionCategory}
                                onAddResolutionClicked={onAddResolutionClicked}
                            />
                        }
                    </Fragment>
                )}
            </div>
        </EditorPageLayout>

        {showConfigurationModal &&
            <ConfigurationModal
                onCloseClicked={() => {
                    if (!setting?.isReturnScoreSaving()) {
                        setShowConfigurationModal(false);
                    }
                }}
                onSaveClicked={async () => {
                    if (!setting?.isReturnScoreSaving()) {
                        await setting?.saveReturnScoreSettings();
                        setShowConfigurationModal(false);
                    }
                }} />
        }
    </>
});

export default ResolutionsList;
